import { LIMONADE_MOTIONLINK_SCHEMA, quaternionToEuler, type MotionLinkMotionType } from '@limonade/eye-core';

export interface MotionLinkVisualizationSample {
  timestampMs: number;
  position: {
    x: number;
    y: number;
    z: number;
  };
  rotation?: {
    pitch?: number;
    yaw?: number;
    roll?: number;
  };
  confidence?: number;
  motionType?: MotionLinkMotionType | string;
}

export interface MotionLinkVisualizationData {
  durationMs: number;
  samples: MotionLinkVisualizationSample[];
  bounds: {
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
  };
  warnings: string[];
}

export interface MotionLinkVisualizationQuality {
  schemaValid: boolean;
  sampleContinuity: 'good' | 'gaps' | 'sparse' | 'empty';
  droppedSampleCount: number;
  timestampGapCount: number;
  averageConfidence: number | null;
  estimatedDistance: number;
  maxJitter: number;
  detectedMotionType: string;
  hasPositionData: boolean;
  hasRotationData: boolean;
  warnings: string[];
}

export interface MotionLinkJsonLoadResult {
  ok: boolean;
  raw: unknown;
  data: MotionLinkVisualizationData;
  quality: MotionLinkVisualizationQuality;
  warnings: string[];
}

type AnyRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is AnyRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

function numberOrNull(value: unknown): number | null {
  return isFiniteNumber(value) ? value : null;
}

function recordAt(value: unknown, key: string): AnyRecord | null {
  if (!isRecord(value)) return null;
  const child = value[key];
  return isRecord(child) ? child : null;
}

function vectorFrom(value: unknown): { x: number; y: number; z: number } | null {
  if (!isRecord(value)) return null;
  const x = numberOrNull(value.x);
  const y = numberOrNull(value.y);
  const z = numberOrNull(value.z);
  if (x === null || y === null || z === null) return null;
  return { x, y, z };
}

function quaternionFrom(value: unknown): { x: number; y: number; z: number; w: number } | null {
  if (!isRecord(value)) return null;
  const x = numberOrNull(value.x);
  const y = numberOrNull(value.y);
  const z = numberOrNull(value.z);
  const w = numberOrNull(value.w);
  if (x === null || y === null || z === null || w === null) return null;
  return { x, y, z, w };
}

function eulerFrom(value: unknown): MotionLinkVisualizationSample['rotation'] | undefined {
  if (!isRecord(value)) return undefined;
  const pitch = numberOrNull(value.pitch);
  const yaw = numberOrNull(value.yaw);
  const roll = numberOrNull(value.roll);
  if (pitch === null && yaw === null && roll === null) return undefined;
  return {
    ...(pitch === null ? {} : { pitch }),
    ...(yaw === null ? {} : { yaw }),
    ...(roll === null ? {} : { roll }),
  };
}

function timestampFrom(sample: AnyRecord): number | null {
  return numberOrNull(sample.timestamp_ms) ??
    numberOrNull(sample.timestampMs) ??
    numberOrNull(recordAt(sample, 'camera_motion_estimate')?.timestamp_ms) ??
    numberOrNull(recordAt(sample, 'arkit_pose')?.timestamp_ms) ??
    numberOrNull(recordAt(sample, 'arkit')?.timestamp_ms) ??
    numberOrNull(recordAt(sample, 'imu')?.timestamp_ms);
}

function positionFrom(sample: AnyRecord): { x: number; y: number; z: number } | null {
  return vectorFrom(sample.position) ??
    vectorFrom(recordAt(sample, 'camera_motion_estimate')?.position) ??
    vectorFrom(recordAt(sample, 'arkit_pose')?.position) ??
    vectorFrom(recordAt(sample, 'arkit')?.position);
}

function velocityFrom(sample: AnyRecord): { x: number; y: number; z: number } | null {
  return vectorFrom(sample.velocity) ??
    vectorFrom(recordAt(sample, 'camera_motion_estimate')?.velocity) ??
    vectorFrom(recordAt(sample, 'arkit_pose')?.velocity);
}

function deltaFrom(sample: AnyRecord): { x: number; y: number; z: number } | null {
  return vectorFrom(sample.delta_position) ??
    vectorFrom(sample.position_delta) ??
    vectorFrom(sample.translation_delta) ??
    vectorFrom(recordAt(sample, 'camera_motion_estimate')?.delta_position);
}

function rotationFrom(sample: AnyRecord): MotionLinkVisualizationSample['rotation'] | undefined {
  const directEuler = eulerFrom(sample.rotation_euler) ??
    eulerFrom(recordAt(sample, 'camera_motion_estimate')?.rotation_euler) ??
    eulerFrom(recordAt(sample, 'arkit_pose')?.rotation_euler) ??
    eulerFrom(recordAt(sample, 'imu')?.rotation_euler) ??
    eulerFrom(recordAt(sample, 'imu')?.euler);
  if (directEuler) return directEuler;

  const quaternion = quaternionFrom(sample.rotation_quaternion) ??
    quaternionFrom(recordAt(sample, 'camera_motion_estimate')?.rotation_quaternion) ??
    quaternionFrom(recordAt(sample, 'arkit_pose')?.rotation_quaternion) ??
    quaternionFrom(recordAt(sample, 'arkit')?.orientation_quaternion) ??
    quaternionFrom(recordAt(sample, 'imu')?.rotation_quaternion) ??
    quaternionFrom(recordAt(sample, 'imu')?.attitude_quaternion);
  if (!quaternion) return undefined;
  const euler = quaternionToEuler(quaternion);
  return { pitch: euler.pitch, yaw: euler.yaw, roll: euler.roll };
}

function confidenceFrom(sample: AnyRecord): number | undefined {
  const confidence = numberOrNull(sample.confidence) ??
    numberOrNull(sample.tracking_confidence) ??
    numberOrNull(recordAt(sample, 'camera_motion_estimate')?.confidence) ??
    numberOrNull(recordAt(sample, 'camera_motion_estimate')?.tracking_confidence) ??
    numberOrNull(recordAt(sample, 'arkit_pose')?.tracking_confidence) ??
    numberOrNull(recordAt(sample, 'arkit')?.tracking_confidence) ??
    numberOrNull(recordAt(sample, 'imu')?.tracking_confidence);
  return confidence === null ? undefined : clamp01(confidence);
}

function motionTypeFrom(sample: AnyRecord): string | undefined {
  const values = [
    sample.motion_type,
    recordAt(sample, 'camera_motion_estimate')?.motion_type,
    recordAt(sample, 'arkit_pose')?.motion_type,
    recordAt(sample, 'imu')?.motion_type,
  ];
  return values.find((value): value is string => typeof value === 'string');
}

function rawSamplesFrom(input: unknown): AnyRecord[] {
  if (!isRecord(input)) return [];
  if (Array.isArray(input.samples)) return input.samples.filter(isRecord);
  if (Array.isArray(input.estimates)) return input.estimates.filter(isRecord);
  if (Array.isArray(input.frames)) return input.frames.filter(isRecord);
  return [];
}

function emptyData(warnings: string[] = []): MotionLinkVisualizationData {
  return {
    durationMs: 0,
    samples: [],
    bounds: { minX: 0, maxX: 0, minZ: 0, maxZ: 0 },
    warnings,
  };
}

function calculateBounds(samples: readonly MotionLinkVisualizationSample[]): MotionLinkVisualizationData['bounds'] {
  if (!samples.length) return { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };
  return {
    minX: Math.min(...samples.map((sample) => sample.position.x)),
    maxX: Math.max(...samples.map((sample) => sample.position.x)),
    minZ: Math.min(...samples.map((sample) => sample.position.z)),
    maxZ: Math.max(...samples.map((sample) => sample.position.z)),
  };
}

function estimatedDistance(samples: readonly MotionLinkVisualizationSample[]): number {
  let distance = 0;
  for (let index = 1; index < samples.length; index++) {
    const previous = samples[index - 1]!.position;
    const current = samples[index]!.position;
    distance += Math.hypot(current.x - previous.x, current.y - previous.y, current.z - previous.z);
  }
  return distance;
}

function maxJitter(samples: readonly MotionLinkVisualizationSample[]): number {
  if (samples.length < 3) return 0;
  let max = 0;
  for (let index = 1; index < samples.length - 1; index++) {
    const previous = samples[index - 1]!.position;
    const current = samples[index]!.position;
    const next = samples[index + 1]!.position;
    const expected = {
      x: (previous.x + next.x) / 2,
      y: (previous.y + next.y) / 2,
      z: (previous.z + next.z) / 2,
    };
    max = Math.max(max, Math.hypot(current.x - expected.x, current.y - expected.y, current.z - expected.z));
  }
  return max;
}

function mostCommonMotionType(samples: readonly MotionLinkVisualizationSample[], fallback: string): string {
  const counts = new Map<string, number>();
  for (const sample of samples) {
    if (!sample.motionType || sample.motionType === 'unknown') continue;
    counts.set(sample.motionType, (counts.get(sample.motionType) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? fallback;
}

function qualityFor(
  schemaValid: boolean,
  samples: readonly MotionLinkVisualizationSample[],
  hasPositionData: boolean,
  hasRotationData: boolean,
  warnings: string[],
  detectedMotionType: string,
): MotionLinkVisualizationQuality {
  const gaps: number[] = [];
  const deltas: number[] = [];
  for (let index = 1; index < samples.length; index++) {
    deltas.push(samples[index]!.timestampMs - samples[index - 1]!.timestampMs);
  }
  if (deltas.length) {
    const median = [...deltas].sort((a, b) => a - b)[Math.floor(deltas.length / 2)] ?? 0;
    deltas.forEach((delta) => {
      if (median > 0 && delta > Math.max(median * 2.5, median + 120)) gaps.push(delta);
    });
  }

  const confidences = samples
    .map((sample) => sample.confidence)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  const averageConfidence = confidences.length
    ? confidences.reduce((sum, value) => sum + value, 0) / confidences.length
    : null;
  const distance = estimatedDistance(samples);
  const jitter = maxJitter(samples);
  const qualityWarnings = [...warnings];

  if (!samples.length) qualityWarnings.push('No motion samples were found.');
  if (samples.length > 0 && samples.length < 3) qualityWarnings.push('Sparse motion data; visual preview may be limited.');
  if (gaps.length) qualityWarnings.push('Timestamp gaps detected; capture continuity may be uneven.');
  if (averageConfidence !== null && averageConfidence < 0.45) qualityWarnings.push('Low average tracking confidence.');
  if (!hasPositionData && hasRotationData) qualityWarnings.push('Orientation-only data; camera path will stay in place.');
  if (!hasPositionData) qualityWarnings.push('No position data; using a normalized fallback path when possible.');
  if (jitter > Math.max(distance * 0.25, 0.08)) qualityWarnings.push('Excessive jitter detected in the visualized path.');
  if (distance > 0.4 && samples.length > 4) {
    const first = samples[0]!.position;
    const last = samples[samples.length - 1]!.position;
    const netDistance = Math.hypot(last.x - first.x, last.y - first.y, last.z - first.z);
    if (netDistance < distance * 0.18) qualityWarnings.push('Path appears to drift or return heavily; review calibration and mount stability.');
  }

  return {
    schemaValid,
    sampleContinuity: samples.length === 0 ? 'empty' : samples.length < 3 ? 'sparse' : gaps.length ? 'gaps' : 'good',
    droppedSampleCount: gaps.length,
    timestampGapCount: gaps.length,
    averageConfidence,
    estimatedDistance: distance,
    maxJitter: jitter,
    detectedMotionType,
    hasPositionData,
    hasRotationData,
    warnings: [...new Set(qualityWarnings)],
  };
}

export function interpolateMotionLinkSample(
  samples: readonly MotionLinkVisualizationSample[],
  timestampMs: number,
): MotionLinkVisualizationSample | null {
  if (!samples.length) return null;
  if (timestampMs <= samples[0]!.timestampMs) return samples[0]!;
  if (timestampMs >= samples[samples.length - 1]!.timestampMs) return samples[samples.length - 1]!;
  let rightIndex = samples.findIndex((sample) => sample.timestampMs >= timestampMs);
  if (rightIndex <= 0) rightIndex = 1;
  const left = samples[rightIndex - 1]!;
  const right = samples[rightIndex]!;
  const span = Math.max(1e-6, right.timestampMs - left.timestampMs);
  const t = Math.max(0, Math.min(1, (timestampMs - left.timestampMs) / span));
  const mix = (a: number, b: number): number => a + (b - a) * t;
  const rotation = left.rotation || right.rotation
    ? {
        pitch: mix(left.rotation?.pitch ?? right.rotation?.pitch ?? 0, right.rotation?.pitch ?? left.rotation?.pitch ?? 0),
        yaw: mix(left.rotation?.yaw ?? right.rotation?.yaw ?? 0, right.rotation?.yaw ?? left.rotation?.yaw ?? 0),
        roll: mix(left.rotation?.roll ?? right.rotation?.roll ?? 0, right.rotation?.roll ?? left.rotation?.roll ?? 0),
      }
    : undefined;
  const confidence = left.confidence !== undefined || right.confidence !== undefined
    ? mix(left.confidence ?? right.confidence ?? 0, right.confidence ?? left.confidence ?? 0)
    : undefined;
  return {
    timestampMs,
    position: {
      x: mix(left.position.x, right.position.x),
      y: mix(left.position.y, right.position.y),
      z: mix(left.position.z, right.position.z),
    },
    ...(rotation ? { rotation } : {}),
    ...(confidence === undefined ? {} : { confidence }),
    motionType: left.motionType ?? right.motionType,
  };
}

export class MotionLinkJsonLoader {
  loadText(text: string): MotionLinkJsonLoadResult {
    try {
      return this.load(JSON.parse(text) as unknown);
    } catch {
      const warnings = ['Invalid JSON; unable to parse MotionLink data.'];
      const data = emptyData(warnings);
      return {
        ok: false,
        raw: null,
        data,
        warnings,
        quality: qualityFor(false, [], false, false, warnings, 'unknown'),
      };
    }
  }

  load(input: unknown): MotionLinkJsonLoadResult {
    const warnings: string[] = [];
    if (!isRecord(input)) {
      warnings.push('MotionLink document is not a JSON object.');
      const data = emptyData(warnings);
      return {
        ok: false,
        raw: input,
        data,
        warnings,
        quality: qualityFor(false, [], false, false, warnings, 'unknown'),
      };
    }

    const schemaValid = input.schema === LIMONADE_MOTIONLINK_SCHEMA;
    if (!schemaValid) warnings.push('Missing or unsupported schema; expected limonade-motionlink/v1.');

    const rawSamples = rawSamplesFrom(input);
    if (!rawSamples.length) warnings.push('No samples, estimates, or frames array was found.');

    const extracted = rawSamples
      .map((sample) => {
        const timestamp = timestampFrom(sample);
        if (timestamp === null) return null;
        const position = positionFrom(sample);
        const velocity = velocityFrom(sample);
        const delta = deltaFrom(sample);
        return {
          timestamp,
          position,
          velocity,
          delta,
          rotation: rotationFrom(sample),
          confidence: confidenceFrom(sample),
          motionType: motionTypeFrom(sample),
        };
      })
      .filter((sample): sample is NonNullable<typeof sample> => sample !== null)
      .sort((a, b) => a.timestamp - b.timestamp);

    if (extracted.length !== rawSamples.length) warnings.push('Some samples were missing valid timestamps and were skipped.');

    const hasPositionData = extracted.some((sample) => sample.position !== null);
    const hasRotationData = extracted.some((sample) => sample.rotation !== undefined);
    const hasRelativeData = extracted.some((sample) => sample.delta !== null || sample.velocity !== null);

    const pathSourceSamples = hasPositionData
      ? extracted.filter((sample) => sample.position !== null)
      : extracted;
    if (hasPositionData && pathSourceSamples.length !== extracted.length) {
      warnings.push('IMU-only samples ignored for camera path visualization because ARKit position data is available.');
    }

    const firstTimestamp = pathSourceSamples[0]?.timestamp ?? extracted[0]?.timestamp ?? 0;
    let fallbackPosition = { x: 0, y: 0, z: 0 };
    let previousTimestamp = firstTimestamp;

    const samples = pathSourceSamples.map((sample): MotionLinkVisualizationSample => {
      const timestampMs = sample.timestamp - firstTimestamp;
      let position = sample.position;
      if (!position) {
        if (sample.delta) {
          fallbackPosition = {
            x: fallbackPosition.x + sample.delta.x,
            y: fallbackPosition.y + sample.delta.y,
            z: fallbackPosition.z + sample.delta.z,
          };
        } else if (sample.velocity) {
          const deltaSeconds = Math.max(0, sample.timestamp - previousTimestamp) / 1000;
          fallbackPosition = {
            x: fallbackPosition.x + sample.velocity.x * deltaSeconds,
            y: fallbackPosition.y + sample.velocity.y * deltaSeconds,
            z: fallbackPosition.z + sample.velocity.z * deltaSeconds,
          };
        }
        position = fallbackPosition;
      }
      previousTimestamp = sample.timestamp;
      return {
        timestampMs,
        position,
        ...(sample.rotation ? { rotation: sample.rotation } : {}),
        ...(sample.confidence === undefined ? {} : { confidence: sample.confidence }),
        ...(sample.motionType ? { motionType: sample.motionType } : {}),
      };
    });

    if (!hasPositionData && hasRelativeData) warnings.push('Using relative velocity/delta fields to generate a normalized path.');
    if (!hasPositionData && !hasRelativeData && hasRotationData) warnings.push('Only rotation data found; preview will animate orientation in place.');

    const durationMs = samples.length ? samples[samples.length - 1]!.timestampMs : 0;
    const bounds = calculateBounds(samples);
    const summary = recordAt(input, 'summary');
    const detectedMotionType = mostCommonMotionType(
      samples,
      typeof summary?.dominant_motion_type === 'string' ? summary.dominant_motion_type : 'unknown',
    );
    const dataWarnings = [...new Set(warnings)];
    const data = {
      durationMs,
      samples,
      bounds,
      warnings: dataWarnings,
    };
    const quality = qualityFor(schemaValid, samples, hasPositionData, hasRotationData, dataWarnings, detectedMotionType);

    return {
      ok: samples.length > 0,
      raw: input,
      data,
      quality,
      warnings: quality.warnings,
    };
  }
}
